/**
 * Product Add-Ons — Dynamic checkbox UI for Handle, LFC, and Gridded Surface.
 *
 * Listens for Dawn's variantChange pub/sub event to show/hide add-on checkboxes
 * with the correct price for the selected variant's tier. On add-to-cart, intercepts
 * the form submission to add both the gage variant AND any checked add-on variants
 * as separate line items via /cart/add.js batch endpoint.
 *
 * Follows Dawn's web component pattern (extends HTMLElement).
 */

if (!customElements.get('product-addons')) {
  customElements.define(
    'product-addons',
    class ProductAddons extends HTMLElement {
      constructor() {
        super();

        this.sectionId = this.dataset.sectionId;
        this.data = this.loadData();
        if (!this.data) return;

        this.handleEl = this.querySelector(`#AddonHandle-${this.sectionId}`);
        this.lfcEl = this.querySelector(`#AddonLfc-${this.sectionId}`);
        this.griddedEl = this.querySelector(`#AddonGridded-${this.sectionId}`);
        this.caseEl = this.querySelector(`#AddonCase-${this.sectionId}`);
        this.riserEl = this.querySelector(`#AddonRiser-${this.sectionId}`);

        this.handleCheckbox = this.handleEl?.querySelector('[data-addon-type="handle"]');
        this.lfcCheckbox = this.lfcEl?.querySelector('[data-addon-type="lfc"]');
        this.griddedCheckbox = this.griddedEl?.querySelector('[data-addon-type="gridded"]');
        this.caseCheckbox = this.caseEl?.querySelector('[data-addon-type="case"]');
        this.riserCheckbox = this.riserEl?.querySelector('[data-addon-type="riser"]');

        // Current add-on variant IDs (updated on variant change)
        this.currentHandleVariantId = null;
        this.currentLfcVariantId = null;
        this.currentCaseVariantId = null;
        this.currentRiserVariantId = null;

        // Subscribe to Dawn's variant change event
        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.onVariantChange.bind(this)
        );

        // On >250-variant products the Liquid-rendered tier map is capped at
        // 250; the variant table publishes the full matrix (with tiers) once
        // it finishes loading — re-resolve the current variant at that point.
        this.lastVariantId = null;
        this.onMatrixLoaded = () => {
          if (this.lastVariantId) this.updateAddons(this.lastVariantId);
        };
        document.addEventListener('variant-table:loaded', this.onMatrixLoaded);

        // Intercept the product form's add-to-cart
        this.setupFormIntercept();

        // Initialize with the current variant (if pre-selected)
        this.initCurrentVariant();
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
        if (this.onMatrixLoaded) {
          document.removeEventListener('variant-table:loaded', this.onMatrixLoaded);
        }
      }

      loadData() {
        const el = document.getElementById(`AddonTierData-${this.sectionId}`);
        if (!el) return null;
        try {
          return JSON.parse(el.textContent);
        } catch (e) {
          console.error('[ProductAddons] Failed to parse tier data:', e);
          return null;
        }
      }

      initCurrentVariant() {
        // Find the currently selected variant ID from the product form
        const form = this.getProductForm();
        if (!form) return;

        const variantInput = form.querySelector('[name="id"]');
        if (variantInput && variantInput.value) {
          this.updateAddons(variantInput.value);
        }
      }

      onVariantChange(event) {
        if (event.data.sectionId !== this.sectionId) return;

        const variant = event.data.variant;
        if (variant) {
          this.updateAddons(String(variant.id));
        } else {
          // No valid variant selected — hide all add-ons
          this.hideAll();
        }
      }

      /**
       * Tier lookup for variants missing from the Liquid JSON map (only
       * happens past Liquid's 250-variant cap): the variant table's fully
       * loaded matrix carries handleTier/lfcTier for every variant.
       */
      matrixTiers(variantId) {
        const matrix = window.variantTableFullMatrix;
        if (!matrix) return null;
        const idNum = parseInt(variantId, 10);
        const v = matrix.find((entry) => entry.id === idNum);
        if (!v) return null;
        return {
          handleTier: v.handleTier || '',
          lfcTier: v.lfcTier || '',
          caseTier: v.caseTier || '',
          riserTier: v.riserTier || '',
        };
      }

      updateAddons(variantId) {
        this.lastVariantId = variantId;
        const tiers = this.data.variantTiers[variantId] || this.matrixTiers(variantId);
        if (!tiers) {
          this.hideAll();
          return;
        }

        let anyVisible = false;

        // Handle add-on
        if (this.handleEl && this.data.addonProducts.handle) {
          const tier = tiers.handleTier;
          const addonVariant = tier ? this.data.addonProducts.handle.variants[tier] : null;

          if (addonVariant) {
            this.querySelector(`#AddonHandlePrice-${this.sectionId}`).textContent =
              `(+${addonVariant.priceFormatted})`;
            this.currentHandleVariantId = addonVariant.id;
            this.handleEl.style.display = '';
            anyVisible = true;
          } else {
            this.handleEl.style.display = 'none';
            this.currentHandleVariantId = null;
            if (this.handleCheckbox) this.handleCheckbox.checked = false;
          }
        }

        // LFC add-on
        if (this.lfcEl && this.data.addonProducts.lfc) {
          const tier = tiers.lfcTier;
          const addonVariant = tier ? this.data.addonProducts.lfc.variants[tier] : null;

          if (addonVariant) {
            this.querySelector(`#AddonLfcPrice-${this.sectionId}`).textContent =
              `(+${addonVariant.priceFormatted})`;
            this.currentLfcVariantId = addonVariant.id;
            this.lfcEl.style.display = '';
            anyVisible = true;
          } else {
            this.lfcEl.style.display = 'none';
            this.currentLfcVariantId = null;
            if (this.lfcCheckbox) this.lfcCheckbox.checked = false;
          }
        }

        // Storage Case add-on
        if (this.caseEl && this.data.addonProducts.case) {
          const tier = tiers.caseTier;
          const addonVariant = tier ? this.data.addonProducts.case.variants[tier] : null;

          if (addonVariant) {
            this.querySelector(`#AddonCasePrice-${this.sectionId}`).textContent =
              `(+${addonVariant.priceFormatted})`;
            this.currentCaseVariantId = addonVariant.id;
            this.caseEl.style.display = '';
            anyVisible = true;
          } else {
            this.caseEl.style.display = 'none';
            this.currentCaseVariantId = null;
            if (this.caseCheckbox) this.caseCheckbox.checked = false;
          }
        }

        // Riser add-on
        if (this.riserEl && this.data.addonProducts.riser) {
          const tier = tiers.riserTier;
          const addonVariant = tier ? this.data.addonProducts.riser.variants[tier] : null;

          if (addonVariant) {
            this.querySelector(`#AddonRiserPrice-${this.sectionId}`).textContent =
              `(+${addonVariant.priceFormatted})`;
            this.currentRiserVariantId = addonVariant.id;
            this.riserEl.style.display = '';
            anyVisible = true;
          } else {
            this.riserEl.style.display = 'none';
            this.currentRiserVariantId = null;
            if (this.riserCheckbox) this.riserCheckbox.checked = false;
          }
        }

        // Gridded is always visible if tagged (no variant dependency)
        if (this.data.addonProducts.gridded?.show) {
          anyVisible = true;
        }

        // Show/hide the entire container
        this.style.display = anyVisible ? '' : 'none';
      }

      hideAll() {
        this.style.display = 'none';
        if (this.handleEl) this.handleEl.style.display = 'none';
        if (this.lfcEl) this.lfcEl.style.display = 'none';
        if (this.caseEl) this.caseEl.style.display = 'none';
        if (this.riserEl) this.riserEl.style.display = 'none';
        if (this.handleCheckbox) this.handleCheckbox.checked = false;
        if (this.lfcCheckbox) this.lfcCheckbox.checked = false;
        if (this.caseCheckbox) this.caseCheckbox.checked = false;
        if (this.riserCheckbox) this.riserCheckbox.checked = false;
        if (this.griddedCheckbox) this.griddedCheckbox.checked = false;
        this.currentHandleVariantId = null;
        this.currentLfcVariantId = null;
        this.currentCaseVariantId = null;
        this.currentRiserVariantId = null;
      }

      getProductForm() {
        return document.getElementById(this.data.productFormId);
      }

      getCheckedAddonItems(quantity) {
        const qty = quantity || 1;
        const items = [];

        if (this.handleCheckbox?.checked && this.currentHandleVariantId) {
          items.push({
            id: this.currentHandleVariantId,
            quantity: qty,
            properties: { '_addon_type': 'Handle' },
          });
        }

        if (this.lfcCheckbox?.checked && this.currentLfcVariantId) {
          items.push({
            id: this.currentLfcVariantId,
            quantity: qty,
            properties: { '_addon_type': 'Long Form Certificate' },
          });
        }

        if (this.caseCheckbox?.checked && this.currentCaseVariantId) {
          items.push({
            id: this.currentCaseVariantId,
            quantity: qty,
            properties: { '_addon_type': 'Storage Case' },
          });
        }

        if (this.riserCheckbox?.checked && this.currentRiserVariantId) {
          items.push({
            id: this.currentRiserVariantId,
            quantity: qty,
            properties: { '_addon_type': 'Riser' },
          });
        }

        if (this.griddedCheckbox?.checked && this.data.addonProducts.gridded?.variantId) {
          items.push({
            id: this.data.addonProducts.gridded.variantId,
            quantity: qty,
            properties: { '_addon_type': 'Gridded Surface' },
          });
        }

        return items;
      }

      /**
       * Intercept the form submit using a capture-phase listener that fires
       * BEFORE Dawn's handler. When add-ons are checked, we stop the event
       * from reaching Dawn and handle the multi-item add ourselves.
       * When no add-ons are checked, we let it pass through to Dawn normally.
       */
      setupFormIntercept() {
        const form = this.getProductForm();
        if (!form) return;

        this.productFormEl = form.closest('product-form');
        if (!this.productFormEl) return;

        const actualForm = this.productFormEl.querySelector('form');
        if (!actualForm) return;

        const addonsComponent = this;

        actualForm.addEventListener('submit', function (evt) {
          // Validate form before proceeding (catches required fields, range inputs, etc.)
          if (!actualForm.checkValidity()) {
            evt.preventDefault();
            actualForm.reportValidity();
            return;
          }

          // Read quantity from the section-level input (it's outside the <form> element)
          const quantityInput = document.getElementById('Quantity-' + addonsComponent.sectionId)
            || document.querySelector('[name="quantity"]');
          const quantity = parseInt(quantityInput?.value) || 1;

          const addonItems = addonsComponent.getCheckedAddonItems(quantity);
          if (addonItems.length === 0) return; // Let Dawn handle it normally

          // Stop Dawn's handler from firing
          evt.preventDefault();
          evt.stopImmediatePropagation();

          // Run our multi-item add-to-cart
          addonsComponent.handleAddToCartWithAddons(addonItems);
        }, { capture: true });
      }

      async handleAddToCartWithAddons(addonItems) {
        const pf = this.productFormEl;
        const submitButton = pf.querySelector('[type="submit"]');
        const spinner = pf.querySelector('.loading__spinner');
        const form = pf.querySelector('form');
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        if (submitButton.getAttribute('aria-disabled') === 'true') return;

        submitButton.setAttribute('aria-disabled', true);
        submitButton.classList.add('loading');
        if (spinner) spinner.classList.remove('hidden');

        try {
          // Step 1: Add the main product
          const formData = new FormData(form);
          const mainResponse = await fetch(`${routes.cart_add_url}.js`, {
            method: 'POST',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              Accept: 'application/json',
            },
            body: formData,
          });
          const mainResult = await mainResponse.json();

          if (mainResult.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: mainResult.errors || mainResult.description,
              message: mainResult.message,
            });
            pf.handleErrorMessage?.(mainResult.description);
            return;
          }

          // Step 2: Add add-on items in a batch
          const addonResponse = await fetch(`${routes.cart_add_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: addonItems }),
          });
          const addonResult = await addonResponse.json();

          if (addonResult.status) {
            console.warn('[ProductAddons] Add-on items failed:', addonResult);
          }

          // Step 3: Refresh cart UI with fresh state (since we did two adds)
          if (cart) {
            const sectionIds = cart.getSectionsToRender().map((s) => s.id);
            cart.setActiveElement(document.activeElement);

            const refreshResponse = await fetch(
              `${routes.cart_url}?sections=${sectionIds.join(',')}`
            );
            const sections = await refreshResponse.json();
            const cartData = { ...mainResult, sections };

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData,
            });

            cart.renderContents(cartData);
          } else {
            window.location = routes.cart_url;
          }
        } catch (e) {
          console.error('[ProductAddons] Error during add-to-cart:', e);
        } finally {
          submitButton.classList.remove('loading');
          if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          submitButton.removeAttribute('aria-disabled');
          if (spinner) spinner.classList.add('hidden');
        }
      }
    }
  );
}
